export function bytesToBits(bytes: Uint8Array): Uint8Array {
  const bits = new Uint8Array(bytes.length * 8);
  for (let byteIndex = 0; byteIndex < bytes.length; byteIndex += 1) {
    const value = bytes[byteIndex]!;
    for (let bit = 0; bit < 8; bit += 1) {
      bits[byteIndex * 8 + bit] = (value >>> (7 - bit)) & 1;
    }
  }
  return bits;
}

export function bitsToBytes(bits: Uint8Array): Uint8Array {
  if (bits.length % 8 !== 0) throw new RangeError("Bit length must be byte-aligned.");
  const bytes = new Uint8Array(bits.length / 8);
  for (let bitIndex = 0; bitIndex < bits.length; bitIndex += 1) {
    bytes[Math.floor(bitIndex / 8)]! |= bits[bitIndex]! << (7 - (bitIndex % 8));
  }
  return bytes;
}

export function packBits(bits: Uint8Array): Uint8Array {
  const bytes = new Uint8Array(Math.ceil(bits.length / 8));
  for (let bitIndex = 0; bitIndex < bits.length; bitIndex += 1) {
    bytes[Math.floor(bitIndex / 8)]! |= bits[bitIndex]! << (7 - (bitIndex % 8));
  }
  return bytes;
}

export function bitsToNumber(bits: Uint8Array): number {
  if (bits.length > 31) throw new RangeError("Number conversion supports at most 31 bits.");
  let value = 0;
  for (const bit of bits) value = value * 2 + bit;
  return value;
}

export function numberToBits(value: number, bitLength: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0 || value >= 2 ** bitLength) {
    throw new RangeError(`${value} does not fit in ${bitLength} bits.`);
  }
  const bits = new Uint8Array(bitLength);
  for (let index = bitLength - 1; index >= 0; index -= 1) {
    bits[index] = value & 1;
    value = Math.floor(value / 2);
  }
  return bits;
}

export function xorBits(left: Uint8Array, right: Uint8Array): Uint8Array {
  if (left.length !== right.length) throw new RangeError("Bit strings must have equal lengths.");
  const result = new Uint8Array(left.length);
  for (let index = 0; index < left.length; index += 1) result[index] = left[index]! ^ right[index]!;
  return result;
}

export function concatBits(...parts: readonly Uint8Array[]): Uint8Array {
  const result = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}
